import {useEffect, useState} from "react";
import {useSelector} from "react-redux";

import {CategoryAPI} from "@/api/category";
import {ClubAPI} from "@/api/club";
import {MatchAPI} from "@/api/match";
import {PlayerAPI} from "@/api/player";
import {ScoutReportAPI} from "@/api/scout_report";
import {StaffAPI} from "@/api/staff";
import {StaffRoleAPI} from "@/api/staff_role";
import {TrainingAPI} from "@/api/training";
import {UserAPI} from "@/api/user";
import {UserRoleAPI} from "@/api/user_role";

import {ERROR_OPERATION} from "@/utils/constants";

// formatted -> formattazione per select
// onlyPro -> solo categorie agonistiche
// ordered -> ordinamento per il campo sorting
export const useCategories = ({
	setLoading,
	handleNotification,
	formatted = false,
	onlyPro = false,
	ordered = "descend",
}) => {
	const [categories, setCategories] = useState([]);

	useEffect(() => {
		getCategories();
	}, []);

	const getCategories = async () => {
		setLoading && setLoading(true);
		try {
			let response = await CategoryAPI.list();

			if (ordered === "descend") {
				response = response.sort((a, b) => {
					if (a.sorting > b.sorting) {
						return 1;
					} else if (a.sorting < b.sorting) {
						return -1;
					} else {
						return 0;
					}
				});
			}

			if (onlyPro) {
				response = response.filter((c) => !c.basicActivities);
			}

			if (formatted) {
				response = response.map((c) => {
					return {
						value: c.id,
						label: c.name,
						...c,
					};
				});
			}

			setCategories(response?.filter((c) => c.name !== "Non categorizzato"));
		} catch (error) {
			handleNotification && handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading && setLoading(false);
		}
	};

	return {categories};
};

export const useClubs = ({setLoading, handleNotification}) => {
	const [clubs, setClubs] = useState([]);

	useEffect(() => {
		getClubs();
	}, []);

	const getClubs = async () => {
		setLoading && setLoading(true);
		try {
			const response = await ClubAPI.list();
			setClubs(response);
		} catch (error) {
			handleNotification && handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading && setLoading(false);
		}
	};

	return {clubs};
};

export const useMatches = ({setLoading, handleNotification, filters = {}}) => {
	const [matches, setMatches] = useState([]);

	useEffect(() => {
		getMatches();
	}, []);

	const getMatches = async () => {
		setLoading && setLoading(true);
		try {
			const response = await MatchAPI.list(filters);
			setMatches(response);
		} catch (error) {
			handleNotification && handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading && setLoading(false);
		}
	};

	return {matches};
};

// filtered -> no interni "esterni"
// goalkeepers -> ruolo portiere
// onlyInternal -> no interni "prestito"/"esterni"
// prospect -> giocatori scouting
export const usePlayers = ({
	setLoading,
	handleNotification,
	chargeData = true,
	filtered = true,
	goalkeepers = true,
	onlyMovementPlayers = false,
	onlyInternal = false,
	prospect,
}) => {
	const seasonId = useSelector((state) => state?.season?.id);
	const [players, setPlayers] = useState([]);

	useEffect(() => {
		if (chargeData) {
			getPlayers();
		}
	}, [chargeData]);

	const getPlayers = async () => {
		setLoading && setLoading(true);
		try {
			let response = await PlayerAPI.list(
				prospect !== undefined ? {prospect, seasonId} : {seasonId},
			);

			if (filtered) {
				response = response.filter((p) => p.status !== "Esterno");
			}

			if (onlyInternal) {
				response = response.filter((p) => p.status === "Interno");
			}

			if (goalkeepers) {
				response = response.filter((p) => p.goalkeeper);
			}

			if (onlyMovementPlayers) {
				response = response.filter((p) => !p.goalkeeper);
			}
			setPlayers(response);
		} catch (error) {
			handleNotification && handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading && setLoading(false);
		}
	};

	return {players};
};

export const useReports = ({setLoading, handleNotification}) => {
	const [reports, setReports] = useState([]);

	useEffect(() => {
		getReports();
	}, []);

	const getReports = async () => {
		setLoading && setLoading(true);
		try {
			const response = await ScoutReportAPI.list();
			setReports(response);
		} catch (error) {
			handleNotification && handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading && setLoading(false);
		}
	};

	return {reports};
};

export const useTrainings = ({
	setLoading,
	handleNotification,
	filters = {},
}) => {
	const [trainings, setTrainings] = useState([]);

	useEffect(() => {
		getTrainings();
	}, []);

	const getTrainings = async () => {
		setLoading && setLoading(true);
		try {
			const response = await TrainingAPI.list(filters);
			setTrainings(response);
		} catch (error) {
			handleNotification && handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading && setLoading(false);
		}
	};

	return {trainings};
};

export const useStaffs = ({
	setLoading,
	handleNotification,
	chargeData = true,
	prospect,
}) => {
	const [staffs, setStaffs] = useState([]);

	useEffect(() => {
		if (chargeData) {
			getStaffs();
		}
	}, [chargeData]);

	const getStaffs = async () => {
		setLoading && setLoading(true);
		try {
			const response = await StaffAPI.list(
				prospect !== undefined ? {prospect} : {},
			);
			setStaffs(response);
		} catch (error) {
			handleNotification && handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading && setLoading(false);
		}
	};

	return {staffs};
};

export const useStaffRoles = ({setLoading, handleNotification}) => {
	const [staffRoles, setStaffRoles] = useState([]);

	useEffect(() => {
		getRoles();
	}, []);

	const getRoles = async () => {
		setLoading && setLoading(true);
		try {
			const response = await StaffRoleAPI.list();
			setStaffRoles(response);
		} catch (error) {
			handleNotification && handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading && setLoading(false);
		}
	};

	return {staffRoles};
};

export const useUserRoles = ({setLoading, handleNotification}) => {
	const [userRoles, setUserRoles] = useState([]);

	useEffect(() => {
		getRoles();
	}, []);

	const getRoles = async () => {
		setLoading && setLoading(true);
		try {
			const response = await UserRoleAPI.list();
			setUserRoles(response);
		} catch (error) {
			handleNotification && handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading && setLoading(false);
		}
	};

	return {userRoles};
};

export const useUsers = ({setLoading, handleNotification}) => {
	const [users, setUsers] = useState([]);

	useEffect(() => {
		getUsers();
	}, []);

	const getUsers = async () => {
		setLoading && setLoading(true);
		try {
			const response = await UserAPI.list();
			setUsers(response);
		} catch (error) {
			handleNotification && handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading && setLoading(false);
		}
	};

	return {users};
};
